import LeftOutlined from '@ant-design/icons/lib/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/lib/icons/RightOutlined';
import { ConfigProvider, Divider, Flex, Form } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  fetchOptions,
  getQuestionnaireFileUrl,
  putQuestionnaire,
} from '@/apis';
import companyLogo from '@/assets/images/company-logo.png';
import unionLogo from '@/assets/images/union-logo.png';
import Logo from '@/components/Logo';
import ProgressBar from '@/components/ProgressBar';
import {
  HamburgerMenu,
  RadioGroup,
  Select,
  TextArea,
  Upload,
} from '@/components/Questionniare';
import { AnswerType, Option, QuestionModule } from '@/enums';
import { useApiHandler, useBreakpoint, useDebounce } from '@/hooks';
import { OptionType, QuestionWithAnswer } from '@/types';
import { formatQuestionNumber } from '@/utils/common';

import * as Style from './CommonModule.style';

interface CommonModuleProps {
  questions: QuestionWithAnswer[];
  currentQuestionIndex: number;
  onCrossModuleNavigation: (direction: 'prev' | 'next') => Promise<void>;
}

const CommonModule = ({
  questions,
  currentQuestionIndex: initialQuestionIndex,
  onCrossModuleNavigation,
}: CommonModuleProps) => {
  const [form] = Form.useForm();
  const [answersMap, setAnswersMap] = useState<Map<string, string>>(new Map());
  const [currentQuestionIndex, setCurrentQuestionIndex] =
    useState(initialQuestionIndex);
  const [currentQuestion, setCurrentQuestion] =
    useState<QuestionWithAnswer | null>(null);
  const [logosVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Breakpoint detection for responsive rendering
  const { isMobileOrTablet } = useBreakpoint();

  const { questionnaireId } = useParams<{ questionnaireId: string }>();

  const { handleApiCall } = useApiHandler();

  const saveAnswerOnly = async (
    questionId: string,
    answer: string,
    industryId?: string,
  ) => {
    try {
      await handleApiCall(
        () =>
          putQuestionnaire(
            {
              questionnaireId: questionnaireId!,
              questionId,
              answer,
              ...(industryId ? { industryId } : {}),
            },
            false,
          ),
        { showLoading: false },
      );
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleBack = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      if (currentQuestionIndex > 0) {
        const newIndex = currentQuestionIndex - 1;
        const newQuestion = questions[newIndex];

        if (newQuestion) {
          await handleApiCall(
            () =>
              putQuestionnaire(
                {
                  questionnaireId: questionnaireId!,
                  currentQuestionId: newQuestion.id,
                },
                false,
              ),
            { showLoading: false },
          );

          setCurrentQuestionIndex(newIndex);
          setCurrentQuestion(newQuestion);
        }
      } else {
        await onCrossModuleNavigation('prev');
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const handleNext = async () => {
    if (isNavigating) return;
    if (!currentQuestion) return;

    setIsNavigating(true);

    try {
      if (currentQuestionIndex < questions.length - 1) {
        const newIndex = currentQuestionIndex + 1;
        const newQuestion = questions[newIndex];

        if (!newQuestion) return;

        if (currentQuestion.answerType === AnswerType.Info) {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    questionId: currentQuestion.id,
                    answer: 'true',
                    currentQuestionId: newQuestion.id,
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to save info question answer:', error);
            return;
          }
        } else {
          try {
            await form.validateFields();
          } catch {
            return;
          }
          const currentAnswer = form.getFieldValue(currentQuestion.id);

          // 處理上傳類型的答案（檔案已在選擇時上傳，這裡只需保存 objectPath）
          if (
            currentQuestion.answerType === AnswerType.Upload &&
            currentAnswer &&
            currentAnswer.startsWith('questionnaire-files/')
          ) {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer,
                      currentQuestionId: newQuestion.id,
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to save upload answer:', error);
              return;
            }
          } else if (currentAnswer !== undefined && currentAnswer !== null) {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer.toString(),
                      currentQuestionId: newQuestion.id,
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error(
                'Failed to save answer and update position:',
                error,
              );
              return;
            }
          } else {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      currentQuestionId: newQuestion.id,
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to update position:', error);
              return;
            }
          }
        }

        setCurrentQuestionIndex(newIndex);
        setCurrentQuestion(newQuestion);
      } else {
        if (currentQuestion.answerType === AnswerType.Info) {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    questionId: currentQuestion.id,
                    answer: 'true',
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to save info question answer:', error);
            return;
          }
        } else {
          try {
            await form.validateFields();
          } catch {
            return;
          }
          const currentAnswer = form.getFieldValue(currentQuestion.id);

          // 處理最後一題的上傳類型答案（檔案已在選擇時上傳，這裡只需保存 objectPath）
          if (
            currentQuestion.answerType === AnswerType.Upload &&
            currentAnswer &&
            currentAnswer.startsWith('questionnaire-files/')
          ) {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer,
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to save upload answer:', error);
              return;
            }
          } else if (
            currentAnswer !== undefined &&
            currentAnswer !== null &&
            currentAnswer !== ''
          ) {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer.toString(),
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to save answer:', error);
              return;
            }
          }
        }

        await onCrossModuleNavigation('next');
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const debouncedSaveAnswer = useDebounce(saveAnswerOnly, 300);

  useEffect(() => {
    if (questions.length > 0) {
      const newAnswersMap = new Map<string, string>();
      const formValues: Record<string, string> = {};

      questions.forEach((q) => {
        if (q.answer != null) {
          formValues[q.id] = q.answer;
          newAnswersMap.set(q.id, q.answer);
        }
      });

      setAnswersMap(newAnswersMap);

      form.setFieldsValue(formValues);

      setCurrentQuestionIndex(initialQuestionIndex);
      setCurrentQuestion(questions[initialQuestionIndex] || null);
      setMounted(true);
    }
  }, [questions, initialQuestionIndex, form]);

  // 載入已上傳的檔案 URL
  useEffect(() => {
    const loadFileUrls = async () => {
      if (!questionnaireId || questions.length === 0) return;

      const uploadQuestions = questions.filter(
        (q) =>
          q.answerType === AnswerType.Upload &&
          q.answer &&
          q.answer.startsWith('questionnaire-files/'),
      );

      if (uploadQuestions.length === 0) return;

      const newFileUrlsMap = new Map<string, string>();

      await Promise.all(
        uploadQuestions.map(async (q) => {
          try {
            const result = await handleApiCall(
              () => getQuestionnaireFileUrl(questionnaireId, q.id, false),
              { showLoading: false },
            );
            if (result?.url) {
              newFileUrlsMap.set(q.id, result.url);
            }
          } catch (error) {
            console.error(
              `Failed to load file URL for question ${q.id}:`,
              error,
            );
          }
        }),
      );
    };

    loadFileUrls();
  }, [questions, questionnaireId]);

  const [operationYearsOptions, setOperationYearsOptions] = useState<
    OptionType[]
  >([]);

  useEffect(() => {
    const init = async () => {
      const options = await handleApiCall(
        () => fetchOptions(Option.OperationYears),
        {
          context: '獲取經營年限選項',
        },
      );
      if (options && options.length > 0) {
        setOperationYearsOptions(options);
      }
    };

    init();
  }, [handleApiCall]);

  useEffect(() => {
    if (currentQuestion) {
      const fieldName = currentQuestion.id;
      const value = answersMap.get(currentQuestion.id) ?? '';
      const currentValue = form.getFieldValue(fieldName);
      if (value !== currentValue) {
        form.setFieldValue(fieldName, value);
      }
    }
  }, [currentQuestion, answersMap, form]);

  // Mobile/Tablet Page - Conditional Rendering
  if (isMobileOrTablet) {
    return (
      currentQuestion &&
      mounted && (
        <Style.MobileContainer>
          <HamburgerMenu
            questions={questions}
            answersMap={answersMap}
            module={QuestionModule.Common}
            currentStep={
              currentQuestion.answerType !== AnswerType.Info
                ? questions
                    .slice(0, currentQuestionIndex + 1)
                    .filter((q) => q.answerType !== AnswerType.Info).length - 1
                : undefined
            }
            totalSteps={
              currentQuestion.answerType !== AnswerType.Info
                ? questions.filter((q) => q.answerType !== AnswerType.Info)
                    .length
                : undefined
            }
            onNavigate={async (index: number, questionId: string) => {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      currentQuestionId: questionId,
                    },
                    false,
                  ),
                { showLoading: false },
              );

              setCurrentQuestionIndex(index);
              const question = questions.find((q) => q.id === questionId);
              setCurrentQuestion(question || null);
            }}
          />

          <Style.MobileContentArea>
            {currentQuestion.answerType !== AnswerType.Info && (
              <>
                <Style.Title>基本資料</Style.Title>
                <Divider size="small" />

                <Style.QuestionNumber>
                  {formatQuestionNumber(currentQuestion.order)}
                </Style.QuestionNumber>
              </>
            )}

            <Style.MobileTitle
              $info={currentQuestion.answerType === AnswerType.Info}
            >
              {currentQuestion.question}
            </Style.MobileTitle>

            <Style.MobileDescription>
              {currentQuestion.information}
            </Style.MobileDescription>

            {currentQuestion.answerType === AnswerType.Info && (
              <Style.MobileLogoArea justify="center" gap={48}>
                <Logo src={companyLogo} alt="Company Logo" height="40px" />
                <Logo src={unionLogo} alt="Union Logo" height="40px" />
              </Style.MobileLogoArea>
            )}

            <Form
              form={form}
              component={false}
              onValuesChange={(changedValues: Record<string, unknown>) => {
                Object.entries(changedValues).forEach(([fieldName, value]) => {
                  if (value != null) {
                    const questionId = fieldName;
                    const valueStr = value.toString();
                    setAnswersMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set(questionId, valueStr);
                      return newMap;
                    });

                    const question = questions.find((q) => q.id === questionId);

                    if (question) {
                      if (question.answerType === AnswerType.Textarea) {
                        debouncedSaveAnswer(questionId, valueStr);
                      } else if (question.answerType !== AnswerType.Info) {
                        saveAnswerOnly(questionId, valueStr);
                      }
                    }
                  }
                });
              }}
            >
              <Style.FormContainer>
                {currentQuestion.answerType === AnswerType.Radio && (
                  <Form.Item
                    name={currentQuestion.id}
                    rules={[
                      {
                        required: currentQuestion.isRequired,
                        message: '此為必填欄位',
                      },
                    ]}
                    className="radio-in-form-item"
                  >
                    <RadioGroup options={operationYearsOptions} />
                  </Form.Item>
                )}

                {currentQuestion.answerType === AnswerType.Select && (
                  <Form.Item
                    name={currentQuestion.id}
                    rules={[
                      {
                        required: currentQuestion.isRequired,
                        message: '此為必填欄位',
                      },
                    ]}
                    className="select-in-form-item"
                  >
                    <Select options={[]} />
                  </Form.Item>
                )}

                {currentQuestion.answerType === AnswerType.Textarea && (
                  <Form.Item
                    name={currentQuestion.id}
                    rules={[
                      {
                        required: currentQuestion.isRequired,
                        message: '此為必填欄位',
                      },
                    ]}
                    className="textarea-in-form-item"
                  >
                    <TextArea />
                  </Form.Item>
                )}

                {currentQuestion.answerType === AnswerType.Upload && (
                  <Flex
                    vertical
                    gap={16}
                    align="center"
                    style={{ marginRight: 'auto' }}
                  >
                    <Form.Item
                      name={currentQuestion.id}
                      rules={[
                        {
                          required: currentQuestion.isRequired,
                          message: '此為必填欄位',
                        },
                      ]}
                      className="upload-in-form-item"
                      style={{ marginBottom: 0 }}
                    >
                      <Upload
                        questionnaireId={questionnaireId}
                        questionId={currentQuestion.id}
                      />
                    </Form.Item>
                  </Flex>
                )}
              </Style.FormContainer>
            </Form>
          </Style.MobileContentArea>

          <Style.MobileButtonArea>
            <Style.MobileBackButtonWrapper>
              <Style.BackButton
                type="text"
                onClick={isNavigating ? undefined : handleBack}
                style={{
                  visibility: currentQuestionIndex > 0 ? 'visible' : 'hidden',
                  position: 'absolute',
                  bottom: '17%',
                  left: '8%',
                }}
              >
                <LeftOutlined />
                <span>BACK</span>
              </Style.BackButton>
            </Style.MobileBackButtonWrapper>

            <Style.MobileNextButtonWrapper>
              <ConfigProvider wave={{ disabled: true }}>
                <Style.NextButton
                  onClick={isNavigating ? undefined : handleNext}
                  $gradient="linear-gradient(#586c64 0%, #8da67f 100%)"
                />
              </ConfigProvider>
              <Style.ButtonText>
                <span>NEXT</span>
                <RightOutlined />
              </Style.ButtonText>
            </Style.MobileNextButtonWrapper>
          </Style.MobileButtonArea>
        </Style.MobileContainer>
      )
    );
  }

  return (
    questions.length > 0 &&
    currentQuestion &&
    mounted && (
      <Style.QuestionnaireContainer>
        <HamburgerMenu
          questions={questions}
          answersMap={answersMap}
          module={QuestionModule.Common}
          onNavigate={async (index: number, questionId: string) => {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    currentQuestionId: questionId,
                  },
                  false,
                ),
              { showLoading: false },
            );

            setCurrentQuestionIndex(index);
            const question = questions.find((q) => q.id === questionId);
            setCurrentQuestion(question || null);
          }}
        />

        {currentQuestion.answerType !== AnswerType.Info && (
          <ProgressBar
            currentStep={
              questions
                .slice(0, currentQuestionIndex + 1)
                .filter((q) => q.answerType !== AnswerType.Info).length - 1
            }
            totalSteps={
              questions.filter((q) => q.answerType !== AnswerType.Info).length
            }
          />
        )}

        <Style.QuestionnaireWrapper vertical justify="space-between">
          <div
            style={{
              display: 'grid',
              gridTemplateRows: 'auto 1fr',
              rowGap: '24px',
              flex: 1,
            }}
          >
            <Style.BackButton
              type="text"
              onClick={isNavigating ? undefined : handleBack}
              style={{
                visibility: currentQuestionIndex > 0 ? 'visible' : 'hidden',
              }}
            >
              <LeftOutlined />
              <span>BACK</span>
            </Style.BackButton>

            {currentQuestion && mounted && (
              <>
                {currentQuestion.answerType === AnswerType.Info ? (
                  <Style.BeforeCommonStageContainer>
                    <Style.InfoTitle>
                      {currentQuestion.question}
                    </Style.InfoTitle>
                    <Style.DescriptionWrapper>
                      <Style.Description>
                        {currentQuestion.information}
                      </Style.Description>
                    </Style.DescriptionWrapper>
                  </Style.BeforeCommonStageContainer>
                ) : (
                  <Style.CommonStageContainer>
                    <Flex vertical gap={16}>
                      <Style.Title>基本資料</Style.Title>
                      <Flex gap="2rem">
                        <Style.QuestionNumber>
                          {formatQuestionNumber(currentQuestion.order)}
                        </Style.QuestionNumber>
                        <Flex vertical justify="center" gap={8}>
                          <Style.QuestionText>
                            {currentQuestion.question}
                          </Style.QuestionText>
                          {currentQuestion.information && (
                            <Style.QuestionInformation>
                              {currentQuestion.information}
                            </Style.QuestionInformation>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>

                    <Form
                      form={form}
                      component={false}
                      onValuesChange={(
                        changedValues: Record<string, unknown>,
                      ) => {
                        Object.entries(changedValues).forEach(
                          ([fieldName, value]) => {
                            if (value != null) {
                              const questionId = fieldName;
                              const valueStr = value.toString();
                              setAnswersMap((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(questionId, valueStr);
                                return newMap;
                              });

                              const question = questions.find(
                                (q) => q.id === questionId,
                              );

                              if (question) {
                                if (
                                  question.answerType === AnswerType.Textarea
                                ) {
                                  debouncedSaveAnswer(questionId, valueStr);
                                } else if (
                                  question.answerType !== AnswerType.Info
                                ) {
                                  saveAnswerOnly(questionId, valueStr);
                                }
                              }
                            }
                          },
                        );
                      }}
                    >
                      <Style.FormContainer>
                        {currentQuestion.answerType === AnswerType.Radio && (
                          <Form.Item
                            name={currentQuestion.id}
                            rules={[
                              {
                                required: currentQuestion.isRequired,
                                message: '此為必填欄位',
                              },
                            ]}
                            className="radio-in-form-item"
                          >
                            <RadioGroup options={operationYearsOptions} />
                          </Form.Item>
                        )}

                        {currentQuestion.answerType === AnswerType.Select && (
                          <Form.Item
                            name={currentQuestion.id}
                            rules={[
                              {
                                required: currentQuestion.isRequired,
                                message: '此為必填欄位',
                              },
                            ]}
                            className="select-in-form-item"
                          >
                            <Select options={[]} />
                          </Form.Item>
                        )}

                        {currentQuestion.answerType === AnswerType.Textarea && (
                          <Form.Item
                            name={currentQuestion.id}
                            rules={[
                              {
                                required: currentQuestion.isRequired,
                                message: '此為必填欄位',
                              },
                            ]}
                            className="textarea-in-form-item"
                          >
                            <TextArea />
                          </Form.Item>
                        )}

                        {currentQuestion.answerType === AnswerType.Upload && (
                          <Flex
                            vertical
                            gap={16}
                            align="center"
                            style={{ marginRight: 'auto' }}
                          >
                            <Form.Item
                              name={currentQuestion.id}
                              rules={[
                                {
                                  required: currentQuestion.isRequired,
                                  message: '此為必填欄位',
                                },
                              ]}
                              className="upload-in-form-item"
                              style={{ marginBottom: 0 }}
                            >
                              <Upload
                                questionnaireId={questionnaireId}
                                questionId={currentQuestion.id}
                              />
                            </Form.Item>
                          </Flex>
                        )}
                      </Style.FormContainer>
                    </Form>
                  </Style.CommonStageContainer>
                )}
              </>
            )}
          </div>

          <Style.LogoContainer
            vertical
            align="start"
            gap={16}
            $show={currentQuestion?.answerType === AnswerType.Info}
            $logosVisible={logosVisible}
          >
            <Logo src={companyLogo} alt="Company Logo" />
            <Logo src={unionLogo} alt="Union Logo" />
          </Style.LogoContainer>
        </Style.QuestionnaireWrapper>

        <ConfigProvider wave={{ disabled: true }}>
          <Style.NextButton
            onClick={isNavigating ? undefined : handleNext}
            $gradient="linear-gradient(#586c64 0%, #8da67f 100%)"
          >
            <Style.ButtonTextWrapper>
              <Style.ButtonText>
                <span>NEXT</span>
                <RightOutlined />
              </Style.ButtonText>
            </Style.ButtonTextWrapper>
          </Style.NextButton>
        </ConfigProvider>
      </Style.QuestionnaireContainer>
    )
  );
};

export default CommonModule;
